import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Hook for a draggable + minimizable floating panel.
 *
 *  - Position is stored in localStorage under `key` so it persists between visits.
 *  - Minimized state is stored under `key + ':min'`.
 *  - Touch + mouse pointer events are handled.
 *  - Panel is clamped to the viewport so it can never be dragged off-screen.
 *
 * Usage:
 *   const { ref, minimized, toggleMin, dragHandleProps } = useDraggablePanel('weather', { x: 16, y: 96 });
 *   return (
 *     <div ref={ref} style={{ position: 'fixed', top: 0, left: 0, transform: `translate(${x}px, ${y}px)` }}>
 *       <div {...dragHandleProps}>Grab handle</div>
 *       <button onClick={toggleMin}>Minimize</button>
 *     </div>
 *   );
 */
export function useDraggablePanel(key, defaultPos = { x: 16, y: 96 }) {
  const ref = useRef(null);
  const posKey = `hln_panel_pos:${key}`;
  const minKey = `hln_panel_min:${key}`;

  const readPos = () => {
    try {
      const raw = localStorage.getItem(posKey);
      if (raw) {
        const p = JSON.parse(raw);
        if (typeof p.x === 'number' && typeof p.y === 'number') return p;
      }
    } catch { /* ignore */ }
    return defaultPos;
  };
  const readMin = () => {
    try { return localStorage.getItem(minKey) === '1'; } catch { return false; }
  };

  const [pos, setPos] = useState(readPos);
  const [minimized, setMinimized] = useState(readMin);
  const [dragging, setDragging] = useState(false);
  const dragState = useRef(null);

  // Persist position + minimized state
  useEffect(() => {
    try { localStorage.setItem(posKey, JSON.stringify(pos)); } catch { /* ignore */ }
  }, [pos, posKey]);
  useEffect(() => {
    try { localStorage.setItem(minKey, minimized ? '1' : '0'); } catch { /* ignore */ }
  }, [minimized, minKey]);

  const clamp = useCallback((x, y) => {
    if (typeof window === 'undefined') return { x, y };
    const el = ref.current;
    const w = el?.offsetWidth || 260;
    const h = el?.offsetHeight || 100;
    const maxX = Math.max(0, window.innerWidth  - w - 8);
    const maxY = Math.max(0, window.innerHeight - h - 8);
    return {
      x: Math.min(Math.max(x, 4), maxX),
      y: Math.min(Math.max(y, 4), maxY),
    };
  }, []);

  const onPointerDown = useCallback((e) => {
    // Ignore right-click and clicks on interactive children
    if (e.button === 2) return;
    const el = ref.current;
    if (!el) return;
    e.preventDefault();
    e.target.setPointerCapture?.(e.pointerId);
    dragState.current = {
      startX: e.clientX,
      startY: e.clientY,
      posX: pos.x,
      posY: pos.y,
    };
    setDragging(true);
  }, [pos]);

  const onPointerMove = useCallback((e) => {
    if (!dragState.current) return;
    const dx = e.clientX - dragState.current.startX;
    const dy = e.clientY - dragState.current.startY;
    setPos(clamp(dragState.current.posX + dx, dragState.current.posY + dy));
  }, [clamp]);

  const onPointerUp = useCallback((e) => {
    if (!dragState.current) return;
    dragState.current = null;
    setDragging(false);
    e.target.releasePointerCapture?.(e.pointerId);
  }, []);

  // Re-clamp on viewport resize so a small screen never leaves the panel off-screen
  useEffect(() => {
    const onResize = () => setPos((p) => clamp(p.x, p.y));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [clamp]);

  return {
    ref,
    pos,
    minimized,
    dragging,
    toggleMin: () => setMinimized((v) => !v),
    resetPos: () => setPos(defaultPos),
    /** Spread onto the drag handle element (typically the panel header). */
    dragHandleProps: {
      onPointerDown,
      onPointerMove,
      onPointerUp,
      onPointerCancel: onPointerUp,
      style: { cursor: dragging ? 'grabbing' : 'grab', touchAction: 'none', userSelect: 'none' },
    },
  };
}
